import { computeShiftedPlatePositions, maxPositionFor, abbreviateLabel } from "@/lib/cartMath";

/**
 * Printlabel voor één CC/DC-kar op een Zebra 102 × 152 mm rolletiket.
 *
 * De informatievolgorde is bewust operationeel:
 *   1. locatie + karnummer (leesbaar op afstand),
 *   2. klant + leverdatum (controle vóór transport),
 *   3. laag- en plaatindeling (bouwen van de kar),
 *   4. totalen (eindcontrole).
 *
 * Alle posities en aantallen komen ongewijzigd uit het bestaande rekenmodel.
 */
export default function CartPrintLabel({ cart, totalCarts, order, customer, cartType = "CC" }) {
    const totalLayers = cart.layers.length;
    const totalUnits = cart.layers.reduce((sum, layer) => sum + layer.quantity, 0);
    const maxPosition = maxPositionFor(cart.risers);
    const gaps = cart.layers.map((layer) => layer.gap_units || layer.stack_rank || 0);
    const platePositions = computeShiftedPlatePositions(gaps);
    const shortOrderId = order.id.slice(0, 6).toUpperCase();
    const hasPartialLayer = cart.layers.some((layer) => layer.is_partial);

    return (
        <article className={`print-page zebra-label bg-white text-black mx-auto ${
            totalLayers >= 9 ? "zebra-label-extra-dense" : totalLayers >= 7 ? "zebra-label-dense" : ""
        }`}>
            <header className="zebra-header">
                <div className="zebra-brand-row">
                    <div className="zebra-wordmark">FLOREFY <span>FLOW</span></div>
                    <div className="zebra-document-type">KARLABEL · {cartType}</div>
                </div>

                <div className="zebra-identity-grid">
                    <div className="zebra-location-block">
                        <div className="zebra-field-label">Locatie</div>
                        <div className="zebra-location-code">{cart.location_code || "—"}</div>
                        <div className="zebra-location-name">{cart.location_name || "Geen locatie"}</div>
                    </div>
                    <div className="zebra-cart-block">
                        <div className="zebra-field-label">Kar</div>
                        <div className="zebra-cart-number">
                            {cart.cart_no}<span>/{totalCarts}</span>
                        </div>
                    </div>
                </div>

                <div className="zebra-customer-block">
                    <div className="zebra-field-label">Klant</div>
                    <div className="zebra-customer">{customer?.name || "Onbekende klant"}</div>
                </div>

                <div className="zebra-meta-grid">
                    <div>
                        <span className="zebra-field-label">Leverdatum</span>
                        <strong>{order.delivery_date || "—"}</strong>
                    </div>
                    <div>
                        <span className="zebra-field-label">Order</span>
                        <strong>#{shortOrderId}</strong>
                    </div>
                </div>

                {cart.risers && (
                    <div className="zebra-risers">
                        OPZETSTUKKEN GEBRUIKEN · {maxPosition} POSITIES
                    </div>
                )}
            </header>

            <div className="zebra-section-heading">
                <span>Laagindeling</span>
                <span>Boven → beneden</span>
            </div>

            <table className="zebra-table">
                <thead>
                    <tr>
                        <th className="zebra-col-layer">Laag</th>
                        <th className="zebra-col-position">Positie</th>
                        <th className="zebra-col-product">Product</th>
                        <th className="zebra-col-qty">Aantal</th>
                    </tr>
                </thead>
                <tbody>
                    {[...cart.layers].reverse().map((layer, index) => {
                        const originalIndex = cart.layers.length - 1 - index;
                        const layerNumber = originalIndex + 1;
                        const isMerged = layer.merged_items && layer.merged_items.length >= 2;
                        const productLabel = isMerged
                            ? `Gemengde laag · ${layer.merged_items.length} soorten`
                            : abbreviateLabel(layer.product_name, layer.product_cultivar);
                        const position = originalIndex === 0
                            ? "BODEM"
                            : `GAT ${platePositions[originalIndex - 1]}`;
                        const rowKey = `${layer.product_id || "merged"}-${originalIndex}`;

                        return (
                            <tr key={rowKey} className={layer.is_partial ? "zebra-row-partial" : ""}>
                                <td className="zebra-layer-number">{layerNumber}</td>
                                <td className="zebra-position-cell">
                                    <strong>{position}</strong>
                                    <span>{layer.height_profile_code}</span>
                                </td>
                                <td className="zebra-product-cell">
                                    <div className="zebra-product-main">{productLabel}</div>
                                    {!isMerged ? (
                                        <div className="zebra-product-detail">
                                            {layer.pot_size || "Pot —"}
                                            {layer.is_partial ? " · DEELS GEVULD" : ""}
                                        </div>
                                    ) : (
                                        <div className="zebra-merged-list">
                                            {layer.merged_items.map((item, itemIndex) => (
                                                <div key={`${item.product_id || itemIndex}-${itemIndex}`}>
                                                    <span>{abbreviateLabel(item.product_name, item.product_cultivar)} · {item.pot_size || "—"}</span>
                                                    <strong>{item.quantity}</strong>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </td>
                                <td className="zebra-quantity">{layer.quantity}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <footer className="zebra-footer">
                <div className="zebra-total-block">
                    <div>
                        <span className="zebra-field-label">Lagen</span>
                        <strong>{totalLayers}</strong>
                    </div>
                    <div>
                        <span className="zebra-field-label">Totaal</span>
                        <strong>{totalUnits} <small>stuks</small></strong>
                    </div>
                </div>
                <div className="zebra-footer-note">
                    <span>{hasPartialLayer ? "LET OP · deels gevulde laag aanwezig" : "Controle compleet"}</span>
                    <span>#{shortOrderId}</span>
                </div>
            </footer>
        </article>
    );
}
